import React, { useMemo, useState } from 'react';
import { useFeatureFilters } from '../hooks/useFeatureFilters';
import {
  featureBoardCounts,
  formatAcChip,
  landAcreageTotal,
  partitionFeatureBoardRows,
} from '../utils/featureBoardRows';
import { FeatureBoardList } from './FeatureBoardList';
import { ClubLogFillDialog } from './ClubLogFillDialog';
import './StandBoardList.css';
import './ClubMapBoards.css';
import './MyGroundBoard.css';

const TYPE_CHIPS = [
  { key: 'Polygon', label: 'AREA' },
  { key: 'LineString', label: 'LINE' },
  { key: 'Point', label: 'MARKER' },
];

export function MyGroundBoard({
  features = [],
  hiddenFeatureIds,
  selectedFeatureId = null,
  hoveredFeatureId = null,
  onSelectFeature,
  onHoverFeature,
  onEditFeature,
  onStartGeometryEdit,
  onDeleteFeature,
  onToggleHidden,
  onFillLogged,
  canEnableFeed = true,
  now,
}) {
  const [logStation, setLogStation] = useState(null);
  const {
    filteredFeatures,
    searchText,
    setSearchText,
    activeTypes,
    toggleType,
    hiddenOnly,
    toggleHidden,
    showAll,
  } = useFeatureFilters(features, { hiddenFeatureIds });

  const partitioned = useMemo(
    () => partitionFeatureBoardRows(filteredFeatures),
    [filteredFeatures]
  );
  const counts = useMemo(
    () => featureBoardCounts(filteredFeatures, { hiddenFeatureIds }),
    [filteredFeatures, hiddenFeatureIds]
  );
  const landAcres = formatAcChip(landAcreageTotal(partitioned.land));
  const landHeader =
    partitioned.land.length > 0
      ? `${partitioned.land.length} · ${landAcres}`
      : null;
  const pointsNeedFill = counts.needFillCount > 0;
  const allActive = activeTypes.size === 0 && !hiddenOnly;

  return (
    <div className="my-ground-board" data-testid="my-ground-board">
      <div className="my-ground-board__toolbar">
        <div className="my-ground-board__lede">
          <span className="my-ground-board__kicker">FEATURES</span>
          <span className="my-ground-board__counts">
            {counts.headline}
            {counts.needFillCount > 0 ? (
              <span className="my-ground-board__need"> · {counts.needFillCount} NEED FILL</span>
            ) : null}
          </span>
        </div>
        <div className="my-ground-board__filters">
          <div className="my-ground-board__chips" role="group" aria-label="Filter features">
            <button
              type="button"
              className={`my-ground-board__chip${allActive ? ' is-active' : ''}`}
              aria-pressed={allActive}
              onClick={showAll}
            >
              ALL
            </button>
            {TYPE_CHIPS.map(({ key, label }) => {
              const active = activeTypes.has(key);
              return (
                <button
                  key={key}
                  type="button"
                  className={`my-ground-board__chip${active ? ' is-active' : ''}`}
                  aria-pressed={active}
                  onClick={() => toggleType(key)}
                >
                  {label}
                </button>
              );
            })}
            <button
              type="button"
              className={`my-ground-board__chip${hiddenOnly ? ' is-active' : ''}`}
              aria-pressed={hiddenOnly}
              onClick={toggleHidden}
            >
              HIDDEN
            </button>
          </div>
          <input
            type="search"
            className="my-ground-board__search"
            placeholder="Search features"
            value={searchText}
            onChange={(event) => setSearchText(event.target.value)}
            aria-label="Search features"
          />
        </div>
      </div>
      <div className="club-map-tab__boards my-ground-board__columns">
        <FeatureBoardList
          ariaLabel="Land"
          title="LAND · AREAS & LINES"
          headerRight={landHeader}
          emptyTestId="feature-board-land-empty"
          emptyCopy="No areas or lines yet. Draw one with the Area or Line tool."
          features={partitioned.land}
          hiddenFeatureIds={hiddenFeatureIds}
          selectedFeatureId={selectedFeatureId}
          hoveredFeatureId={hoveredFeatureId}
          onSelectFeature={onSelectFeature}
          onHoverFeature={onHoverFeature}
          onEditFeature={onEditFeature}
          onStartGeometryEdit={onStartGeometryEdit}
          onDeleteFeature={onDeleteFeature}
          onToggleHidden={onToggleHidden}
          now={now}
        />
        <FeatureBoardList
          ariaLabel="Points"
          title="POINTS · STANDS, FEEDERS & MARKERS"
          headerRight={
            pointsNeedFill
              ? `${counts.needFillCount} NEED FILL`
              : partitioned.points.length > 0
                ? String(partitioned.points.length)
                : null
          }
          headerTone={pointsNeedFill ? 'need' : undefined}
          emptyTestId="feature-board-points-empty"
          emptyCopy="No points yet. Drop a stand, feeder or marker on the map."
          features={partitioned.points}
          hiddenFeatureIds={hiddenFeatureIds}
          selectedFeatureId={selectedFeatureId}
          hoveredFeatureId={hoveredFeatureId}
          onSelectFeature={onSelectFeature}
          onHoverFeature={onHoverFeature}
          onEditFeature={onEditFeature}
          onStartGeometryEdit={onStartGeometryEdit}
          onDeleteFeature={onDeleteFeature}
          onToggleHidden={onToggleHidden}
          onLogFill={setLogStation}
          now={now}
        />
      </div>
      <ClubLogFillDialog
        show={Boolean(logStation)}
        kind="personal"
        station={logStation}
        canEnableFeed={canEnableFeed}
        onHide={() => setLogStation(null)}
        onLogged={onFillLogged}
      />
    </div>
  );
}

export default MyGroundBoard;
